import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useInterval } from 'usehooks-ts';
import { Logger } from '@/idv2/utils/logger';
import parsePermissionError from './parse-permission-error';
export type CameraPermissionState = 'undetected' | 'undefined-navigator' | 'not-allowed' | 'allowed' | 'device-not-found' | 'device-busy' | 'no-video-option' | 'missing-media-devices' | 'other-error';
const PERMISSION_CHECK_INTERVAL = 100;
// The user can fix the device errors in place, e.g. by opening the laptop lid or quitting the app holding the camera.
const RECHECKED_STATES: CameraPermissionState[] = ['undetected', 'not-allowed', 'device-not-found', 'device-busy'];
const useCameraPermission = () => {
  const $ = _c(7);
  const [permissionState, setPermissionState] = useState("undetected");
  const [permissionQueryPending, setPermissionQueryPending] = useState(false);
  const shouldQueryPermission = RECHECKED_STATES.includes(permissionState) && !permissionQueryPending;
  let t0;
  if ($[0] !== permissionState) {
    const handlePermissionError = err => {
      const error = err as DOMException;
      const nextState = parsePermissionError(error);
      setPermissionState(nextState);
      setPermissionQueryPending(false);
      if (nextState !== permissionState) {
        Logger.warn(`Error while retrieving camera permission. Error: ${error.name}`, {
          location: "desktop-selfie"
        });
      }
    };
    t0 = () => {
      if (!navigator?.mediaDevices) {
        handlePermissionError({
          name: "MissingMediaDevices"
        });
        return;
      }
      navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      }).then(stream => {
        stream.getTracks().forEach(_temp);
        setPermissionState("allowed");
        setPermissionQueryPending(false);
      }).catch(handlePermissionError);
    };
    $[0] = permissionState;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const promptPermission = t0;
  let t1;
  if ($[2] !== promptPermission) {
    t1 = () => {
      setPermissionQueryPending(true);
      if (navigator.permissions && typeof navigator.permissions.query === "function") {
        navigator.permissions.query({
          name: "camera"
        }).then(result => {
          if (result.state !== "granted") {
            setPermissionState(_temp2);
          }
          promptPermission();
        }).catch(() => {
          promptPermission();
        });
        return;
      }
      promptPermission();
    };
    $[2] = promptPermission;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useInterval(t1, shouldQueryPermission ? PERMISSION_CHECK_INTERVAL : null);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setPermissionState("undetected");
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const recheckPermission = t2;
  let t3;
  if ($[5] !== permissionState) {
    t3 = {
      permissionState,
      recheckPermission
    };
    $[5] = permissionState;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export default useCameraPermission;
function _temp(track) {
  track.stop();
}
function _temp2(prev) {
  return prev === "undetected" ? "not-allowed" : prev;
}